import { useState, useMemo, useEffect } from "react";
import { useAdminCampaignPackages, CampaignPackageFormData } from "@/hooks/useAdminCampaigns";
import { CampaignPackage } from "@/hooks/useActiveCampaign";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, CalendarIcon, Plus, X, RefreshCw, Loader2 } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useUserTags, getTagColorClass, UserTag } from "@/hooks/useUserTags";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

interface CampaignPackageFormProps {
  campaignId: string;
  package_: CampaignPackage | null;
  onClose: () => void;
}

interface PackageTag {
  id: string;
  name: string;
  color: string;
}

const CampaignPackageForm = ({
  campaignId,
  package_,
  onClose,
}: CampaignPackageFormProps) => {
  const { createPackage, updatePackage } = useAdminCampaignPackages(campaignId);
  const { allTags } = useUserTags();
  const queryClient = useQueryClient();
  const isEditing = !!package_;

  const [formData, setFormData] = useState<CampaignPackageFormData>({
    campaign_id: campaignId,
    name: package_?.name || "",
    description: package_?.description || "",
    price: package_?.price || 0,
    original_value: package_?.original_value || undefined,
    discount_percent: package_?.discount_percent || undefined,
    credits_amount: package_?.credits_amount || 0,
    includes_nimi_plus: package_?.includes_nimi_plus ?? false,
    first_time_only: package_?.first_time_only ?? false,
    display_order: package_?.display_order || 0,
    is_active: package_?.is_active ?? true,
    grants_unlimited_days: package_?.grants_unlimited_days || undefined,
    unlimited_start_date: package_?.unlimited_start_date || undefined,
    unlimited_end_date: package_?.unlimited_end_date || undefined,
    package_type: package_?.package_type || 'one_off',
    subscription_price_monthly: package_?.subscription_price_monthly || undefined,
    subscription_credits_monthly: package_?.subscription_credits_monthly ?? undefined,
    trial_days: package_?.trial_days || undefined,
    partner_code_filter: package_?.partner_code_filter || '',
    tier_slug: package_?.tier_slug || null,
    order_limit: (package_ as any)?.order_limit ?? null,
  });

  const [selectedPackageTags, setSelectedPackageTags] = useState<PackageTag[]>([]);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isSyncingTags, setIsSyncingTags] = useState(false);

  // Fetch purchase count for existing packages
  const { data: purchaseCount } = useQuery({
    queryKey: ["campaign-package-purchases-count", package_?.id],
    queryFn: async () => {
      if (!package_?.id) return 0;
      const { count, error } = await supabase
        .from("campaign_purchases")
        .select("*", { count: "exact", head: true })
        .eq("package_id", package_.id);
      if (error) {
        console.error("Error fetching purchase count:", error);
        return 0;
      }
      return count || 0;
    },
    enabled: !!package_?.id,
  });

  const handleSyncTagsToExistingPurchasers = async () => {
    if (!package_?.id || selectedPackageTags.length === 0) return;
    
    setIsSyncingTags(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-user-actions", {
        body: {
          action: "sync_campaign_package_tags",
          data: { packageId: package_.id },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Tags synced to ${data.usersUpdated} users`);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error: any) {
      console.error("Error syncing tags:", error);
      toast.error(error.message || "Failed to sync tags");
    } finally {
      setIsSyncingTags(false);
    }
  };

  // Determine initial unlimited mode based on existing data
  const getInitialUnlimitedMode = (): 'static' | 'dynamic' | null => {
    if (package_?.grants_unlimited_days && !package_?.unlimited_start_date) return 'dynamic';
    if (package_?.unlimited_start_date || package_?.unlimited_end_date) return 'static';
    return null;
  };
  const [unlimitedMode, setUnlimitedMode] = useState<'static' | 'dynamic' | null>(getInitialUnlimitedMode);
  const isUnlimitedEnabled = unlimitedMode !== null;

  // Load existing package tags when editing
  useEffect(() => {
    if (package_?.id) {
      const loadPackageTags = async () => {
        const { data, error } = await supabase
          .from("campaign_package_tags")
          .select(`
            tag_id,
            tag:user_tags (id, name, color)
          `)
          .eq("package_id", package_.id);

        if (!error && data) {
          const tags = data
            .filter((d: any) => d.tag)
            .map((d: any) => ({
              id: d.tag.id,
              name: d.tag.name,
              color: d.tag.color,
            }));
          setSelectedPackageTags(tags);
        }
      };
      loadPackageTags();
    }
  }, [package_?.id]);

  // Parse dates for calendar
  const unlimitedStartDate = formData.unlimited_start_date 
    ? new Date(formData.unlimited_start_date) 
    : undefined;
  const unlimitedEndDate = formData.unlimited_end_date 
    ? new Date(formData.unlimited_end_date) 
    : undefined;

  // Calculate total days
  const totalDays = useMemo(() => {
    if (unlimitedStartDate && unlimitedEndDate) {
      const days = differenceInDays(unlimitedEndDate, unlimitedStartDate);
      return days > 0 ? days : null;
    }
    return null;
  }, [unlimitedStartDate, unlimitedEndDate]);

  const handleAddPackageTag = (tag: UserTag) => {
    if (!selectedPackageTags.find((t) => t.id === tag.id)) {
      setSelectedPackageTags([...selectedPackageTags, { id: tag.id, name: tag.name, color: tag.color }]);
    }
    setTagPopoverOpen(false);
  };

  const handleRemovePackageTag = (tagId: string) => {
    setSelectedPackageTags(selectedPackageTags.filter((t) => t.id !== tagId));
  };

  const availablePackageTags = allTags.filter((t) => !selectedPackageTags.find((st) => st.id === t.id));

  const savePackageTags = async (packageId: string) => {
    // Delete existing tags
    await supabase
      .from("campaign_package_tags")
      .delete()
      .eq("package_id", packageId);

    // Insert new tags
    if (selectedPackageTags.length > 0) {
      const tagsToInsert = selectedPackageTags.map((tag) => ({
        package_id: packageId,
        tag_id: tag.id,
      }));

      const { error } = await supabase
        .from("campaign_package_tags")
        .insert(tagsToInsert);

      if (error) {
        console.error("Error saving package tags:", error);
        toast.error("Failed to save package tags");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingTags(true);

    // Validate subscription fields
    if (formData.package_type === 'trial_subscription') {
      if (!formData.subscription_price_monthly || formData.subscription_price_monthly <= 0) {
        toast.error("Monthly price is required for subscription packages");
        setIsSavingTags(false);
        return;
      }
    }

    // Clean data based on unlimited mode and package type
    const isSubWithoutTrial = formData.package_type === 'trial_subscription' && (!formData.trial_days || formData.trial_days <= 0);
    const cleanedData = {
      ...formData,
      // For subscriptions without trial, auto-set price to monthly price
      price: isSubWithoutTrial ? (formData.subscription_price_monthly || 0) : formData.price,
      original_value: isSubWithoutTrial ? null : (formData.original_value || null),
      discount_percent: isSubWithoutTrial ? null : (formData.discount_percent || null),
      grants_unlimited_days: unlimitedMode === 'dynamic' ? formData.grants_unlimited_days : null,
      unlimited_start_date: unlimitedMode === 'static' ? formData.unlimited_start_date : null,
      unlimited_end_date: unlimitedMode === 'static' ? formData.unlimited_end_date : null,
      // Clear subscription fields when one_off
      subscription_price_monthly: formData.package_type === 'trial_subscription' ? formData.subscription_price_monthly : null,
      subscription_credits_monthly: formData.package_type === 'trial_subscription' ? formData.subscription_credits_monthly : null,
      trial_days: formData.package_type === 'trial_subscription' ? (formData.trial_days || null) : null,
      partner_code_filter: formData.partner_code_filter || null,
      order_limit: formData.order_limit || null,
      tier_slug: formData.package_type === 'trial_subscription' ? (formData.tier_slug || null) : null,
    };

    try {
      if (isEditing) {
        await updatePackage.mutateAsync({ id: package_.id, data: cleanedData });
        await savePackageTags(package_.id);
      } else {
        const result = await createPackage.mutateAsync(cleanedData);
        if (result?.id) {
          await savePackageTags(result.id);
        }
      }
      onClose();
    } finally {
      setIsSavingTags(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-medium">
          {isEditing ? "Edit Package" : "New Package"}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Package Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="🌱 Explorer"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) =>
                setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
              }
            />
          </div>
        </div>

        {/* Package Type Selector */}
        <div className="space-y-2">
          <Label>Package Type</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={formData.package_type === 'one_off' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFormData({
                  ...formData,
                  package_type: 'one_off',
                  subscription_price_monthly: undefined,
                  subscription_credits_monthly: undefined,
                  trial_days: undefined,
                });
              }}
            >
              One-Off
            </Button>
            <Button
              type="button"
              variant={formData.package_type === 'trial_subscription' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFormData({
                  ...formData,
                  package_type: 'trial_subscription',
                });
              }}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Subscription
            </Button>
          </div>
        </div>

        {/* Subscription Fields */}
        {formData.package_type === 'trial_subscription' && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <Label className="text-sm font-medium">Subscription Settings</Label>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subscription_price_monthly">Monthly Price (£) *</Label>
                <Input
                  id="subscription_price_monthly"
                  type="number"
                  step="0.01"
                  value={formData.subscription_price_monthly || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subscription_price_monthly: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  placeholder="35.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscription_credits_monthly">Monthly Credits</Label>
                <Input
                  id="subscription_credits_monthly"
                  type="number"
                  value={formData.subscription_credits_monthly ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subscription_credits_monthly: e.target.value !== "" ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="4"
                />
                <p className="text-xs text-muted-foreground">Set to 0 if this tier doesn't include monthly credits</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trial_days">Trial Days</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="0"
                  value={formData.trial_days ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trial_days: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Set to 0 for a direct monthly subscription with no trial window
                </p>
              </div>
            </div>

            {/* Membership Tier */}
            <div className="space-y-2">
              <Label htmlFor="tier_slug">Membership Tier</Label>
              <select
                id="tier_slug"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={formData.tier_slug || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tier_slug: e.target.value || null,
                  })
                }
              >
                <option value="">None</option>
                <option value="friends">Nimi Friends</option>
                <option value="plus">Nimi Plus</option>
                <option value="all_access">Nimi All Access</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Links this subscription to a membership tier for auto-redeeming partner codes
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description || ""}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="One month unlimited Nimi socials"
            rows={2}
          />
        </div>

        {/* Hide trial pricing fields for subscriptions without a trial period */}
        {(() => {
          const isSubWithoutTrial = formData.package_type === 'trial_subscription' && (!formData.trial_days || formData.trial_days <= 0);
          return (
            <div className={cn("grid gap-4", isSubWithoutTrial ? "grid-cols-1 max-w-[200px]" : "grid-cols-4")}>
              {!isSubWithoutTrial && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="price">
                      {formData.package_type === 'trial_subscription' && formData.trial_days && formData.trial_days > 0
                        ? "Trial Price (£) *"
                        : "Price (£) *"}
                    </Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="original_value">Original Value (£)</Label>
                    <Input
                      id="original_value"
                      type="number"
                      step="0.01"
                      value={formData.original_value || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          original_value: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discount_percent">Discount %</Label>
                    <Input
                      id="discount_percent"
                      type="number"
                      value={formData.discount_percent || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          discount_percent: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="credits_amount">
                  {formData.package_type === 'trial_subscription' ? "Initial Credits (one-time)" : "Credits"}
                </Label>
                <Input
                  id="credits_amount"
                  type="number"
                  value={formData.credits_amount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      credits_amount: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          );
        })()}

        {/* Unlimited Access Toggle and Options */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <div className="flex items-center gap-3">
            <Switch
              id="grants_unlimited"
              checked={isUnlimitedEnabled}
              onCheckedChange={(checked) => {
                if (checked) {
                  // Default to dynamic mode with 30 days
                  setUnlimitedMode('dynamic');
                  setFormData({
                    ...formData,
                    grants_unlimited_days: 30,
                    unlimited_start_date: undefined,
                    unlimited_end_date: undefined,
                  });
                } else {
                  setUnlimitedMode(null);
                  setFormData({
                    ...formData,
                    grants_unlimited_days: undefined,
                    unlimited_start_date: undefined,
                    unlimited_end_date: undefined,
                  });
                }
              }}
            />
            <Label htmlFor="grants_unlimited">Grants Unlimited Access</Label>
            {formData.package_type === 'trial_subscription' && (
              <span className="text-xs text-muted-foreground">
                (unlimited booking access during trial)
              </span>
            )}
            {unlimitedMode === 'dynamic' && formData.grants_unlimited_days && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {formData.grants_unlimited_days} days
              </span>
            )}
            {unlimitedMode === 'static' && totalDays !== null && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {totalDays} days
              </span>
            )}
          </div>
          
          {isUnlimitedEnabled && (
            <div className="space-y-4">
              {/* Mode selector */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={unlimitedMode === 'dynamic' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setUnlimitedMode('dynamic');
                    setFormData({
                      ...formData,
                      grants_unlimited_days: formData.grants_unlimited_days || 30,
                      unlimited_start_date: undefined,
                      unlimited_end_date: undefined,
                    });
                  }}
                >
                  From Purchase Date
                </Button>
                <Button
                  type="button"
                  variant={unlimitedMode === 'static' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const endDate = new Date(today);
                    endDate.setDate(endDate.getDate() + (formData.grants_unlimited_days || 30));
                    setUnlimitedMode('static');
                    setFormData({
                      ...formData,
                      grants_unlimited_days: undefined,
                      unlimited_start_date: formData.unlimited_start_date || today.toISOString(),
                      unlimited_end_date: formData.unlimited_end_date || endDate.toISOString(),
                    });
                  }}
                >
                  Fixed Dates
                </Button>
              </div>

              {unlimitedMode === 'dynamic' ? (
                <div className="space-y-2">
                  <Label htmlFor="unlimited_days">Duration (days)</Label>
                  <Input
                    id="unlimited_days"
                    type="number"
                    min="1"
                    value={formData.grants_unlimited_days || 30}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        grants_unlimited_days: parseInt(e.target.value) || 30,
                      })
                    }
                    className="w-32"
                  />
                  <p className="text-xs text-muted-foreground">
                    Unlimited access starts on purchase date and lasts for this many days
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !unlimitedStartDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {unlimitedStartDate ? format(unlimitedStartDate, "PPP") : "Pick start date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={unlimitedStartDate}
                          onSelect={(date) =>
                            setFormData({
                              ...formData,
                              unlimited_start_date: date ? date.toISOString() : undefined,
                            })
                          }
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !unlimitedEndDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {unlimitedEndDate ? format(unlimitedEndDate, "PPP") : "Pick end date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={unlimitedEndDate}
                          onSelect={(date) =>
                            setFormData({
                              ...formData,
                              unlimited_end_date: date ? date.toISOString() : undefined,
                            })
                          }
                          disabled={(date) => unlimitedStartDate ? date < unlimitedStartDate : false}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}
            </div>
          )}
          {formData.package_type === 'trial_subscription' && isUnlimitedEnabled && (
            <p className="text-xs text-muted-foreground border-t pt-2">
              During the trial period, the subscriber gets unlimited access. After trial ends, they receive the monthly credits above on each renewal.
            </p>
          )}
        </div>

        {/* Auto-assign Tags Section */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <Label>Auto-assign Tags on Purchase</Label>
          <div className="flex flex-wrap gap-2 min-h-[36px]">
            {selectedPackageTags.map((tag) => (
              <Badge 
                key={tag.id} 
                variant="outline"
                className={cn("flex items-center gap-1", getTagColorClass(tag.color))}
              >
                {tag.name}
                <button 
                  type="button"
                  onClick={() => handleRemovePackageTag(tag.id)} 
                  className="hover:opacity-70"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
              <PopoverTrigger asChild>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2"
                  disabled={availablePackageTags.length === 0}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="start">
                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                  {availablePackageTags.map((tag) => (
                    <Button
                      key={tag.id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="justify-start"
                      onClick={() => handleAddPackageTag(tag)}
                    >
                      <Badge 
                        variant="outline" 
                        className={cn("text-xs", getTagColorClass(tag.color))}
                      >
                        {tag.name}
                      </Badge>
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-xs text-muted-foreground">
            Users who purchase this package will automatically receive these tags
          </p>
          
          {/* Retroactive sync button - only show when editing with existing purchases */}
          {isEditing && purchaseCount !== undefined && purchaseCount > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium">{purchaseCount}</span>
                  <span className="text-muted-foreground"> existing purchases</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncTagsToExistingPurchasers}
                  disabled={isSyncingTags || selectedPackageTags.length === 0}
                  className="gap-2"
                >
                  {isSyncingTags ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Sync Tags to Existing Purchasers
                </Button>
              </div>
              {selectedPackageTags.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Add tags above first to enable sync
                </p>
              )}
            </div>
          )}
        </div>

        {/* Order Limit */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <Label htmlFor="order_limit">Order Limit</Label>
          <Input
            id="order_limit"
            type="number"
            min={1}
            value={formData.order_limit ?? ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                order_limit: e.target.value === "" ? null : Math.max(1, parseInt(e.target.value, 10) || 1),
              })
            }
            placeholder="Leave blank for unlimited"
          />
          <p className="text-xs text-muted-foreground">
            Max number of buyers. Once reached, the package shows "Sold Out" and can't be bought.
          </p>
        </div>

        {/* Partner Code Filter */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <Label htmlFor="partner_code_filter">Partner Code Filter</Label>
          <Input
            id="partner_code_filter"
            value={formData.partner_code_filter || ""}
            onChange={(e) =>
              setFormData({ ...formData, partner_code_filter: e.target.value })
            }
            placeholder="e.g. PARTNER2025"
          />
          <p className="text-xs text-muted-foreground">
            If set, this package only appears on the Offers page when <code className="bg-muted px-1 rounded">?partnerCode=VALUE</code> is in the URL
          </p>
        </div>

        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_active: checked })
              }
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="first_time_only"
              checked={formData.first_time_only}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, first_time_only: checked })
              }
            />
            <Label htmlFor="first_time_only">New Members Only</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="includes_nimi_plus"
              checked={formData.includes_nimi_plus}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, includes_nimi_plus: checked })
              }
            />
            <Label htmlFor="includes_nimi_plus">Includes Nimi+</Label>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={createPackage.isPending || updatePackage.isPending || isSavingTags}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default CampaignPackageForm;
